/* ═══════════════════════════════════════════════════════════════════════
 * econEngine.js — Demo lease data, econ calculations, reasoning engine
 *
 * 5 leases: 2 profitable, 1 marginal, 2 unprofitable
 * Each lease carries full production history + forecast + chart data
 * ═══════════════════════════════════════════════════════════════════════ */

// ─── Deterministic production generator ───
function gen(base, days, decline, noise, seed) {
  const out = [];
  for (let i = 0; i < days; i++) {
    const d = Math.exp(-decline * i / 365);
    const n = 1 + Math.sin((i + seed) * 0.8) * noise
                + Math.sin((i + seed) * 2.1) * noise * 0.3;
    out.push(Math.max(0.1, base * d * n));
  }
  return out;
}
function genGrowing(base, days, rate, noise, seed) {
  const out = [];
  for (let i = 0; i < days; i++) {
    const g = 1 + rate * i / 365;
    const n = 1 + Math.sin((i + seed) * 0.8) * noise
                + Math.sin((i + seed) * 1.7) * noise * 0.4;
    out.push(Math.max(0.1, base * g * n));
  }
  return out;
}

// Build 120-day series (60 hist + 60 forecast) and derive SVG chart points
function buildChartData(cfg) {
  const totalDays = 120;
  const todayIdx = 60;
  const oil = gen(cfg.oilBase, totalDays, cfg.oilDecline, cfg.oilNoise, cfg.seed);
  const gas = gen(cfg.gasBase, totalDays, cfg.gasDecline, cfg.gasNoise, cfg.seed + 100);
  const water = cfg.waterGrowth > 0
    ? genGrowing(cfg.waterBase, totalDays, cfg.waterGrowth, cfg.waterNoise, cfg.seed + 200)
    : gen(cfg.waterBase, totalDays, Math.abs(cfg.waterDecline || 0.02), cfg.waterNoise, cfg.seed + 200);

  // max values for Y scaling (SVG Y: 30=top, 370=bottom)
  const allVals = [...oil, ...gas, ...water];
  const maxVal = Math.max(...allVals, 1);

  const toY = (v) => 370 - (v / maxVal) * 320;

  // Sample ~12 points per stream for smooth SVG curves
  const sampleIdxs = (start, end, count) => {
    const step = Math.max(1, Math.floor((end - start) / (count - 1)));
    const idxs = [];
    for (let i = start; i < end; i += step) idxs.push(i);
    if (idxs[idxs.length - 1] !== end - 1) idxs.push(end - 1);
    return idxs;
  };

  const histIdxs = sampleIdxs(0, todayIdx, 10);
  const fcstIdxs = sampleIdxs(todayIdx, totalDays, 8);

  const toSvgX = (i) => 60 + (i / (totalDays - 1)) * 700;

  const makePts = (arr, idxs) => idxs.map(i => [toSvgX(i), toY(arr[i])]);

  // P10/P90 bands: scale from p50
  const bandPts = (arr, idxs, mult) => idxs.map(i => [toSvgX(i), toY(arr[i] * mult)]);

  // Build smooth SVG path from points using cubic bezier
  const ptsToPath = (pts) => {
    if (pts.length < 2) return "";
    let d = `M${pts[0][0].toFixed(0)},${pts[0][1].toFixed(0)}`;
    for (let i = 1; i < pts.length; i++) {
      const [px, py] = pts[i - 1];
      const [cx, cy] = pts[i];
      const dx = (cx - px) * 0.4;
      d += ` C${(px + dx).toFixed(0)},${py.toFixed(0)} ${(cx - dx).toFixed(0)},${cy.toFixed(0)} ${cx.toFixed(0)},${cy.toFixed(0)}`;
    }
    return d;
  };

  const todayX = toSvgX(todayIdx);

  // Build interpolation point arrays for hover (full 120 days, sampled every 5)
  const hoverIdxs = sampleIdxs(0, totalDays, 24);
  const oilHoverPts = hoverIdxs.map(i => [toSvgX(i), toY(oil[i])]);
  const gasHoverPts = hoverIdxs.map(i => [toSvgX(i), toY(gas[i])]);
  const watHoverPts = hoverIdxs.map(i => [toSvgX(i), toY(water[i])]);

  // Production value at hover (not Y coord)
  const oilValPts = hoverIdxs.map(i => [toSvgX(i), oil[i]]);
  const gasValPts = hoverIdxs.map(i => [toSvgX(i), gas[i]]);
  const watValPts = hoverIdxs.map(i => [toSvgX(i), water[i]]);

  // Moving average (7-day)
  const ma = (arr) => {
    const out = [];
    for (let i = 0; i < arr.length; i++) {
      const start = Math.max(0, i - 6);
      let sum = 0, cnt = 0;
      for (let j = start; j <= i; j++) { sum += arr[j]; cnt++; }
      out.push(sum / cnt);
    }
    return out;
  };
  const oilMA = ma(oil);
  const gasMA = ma(gas);
  const watMA = ma(water);
  const maIdxs = sampleIdxs(6, todayIdx, 10);
  const oilMAPts = maIdxs.map(i => [toSvgX(i), toY(oilMA[i])]);
  const gasMAPts = maIdxs.map(i => [toSvgX(i), toY(gasMA[i])]);
  const watMAPts = maIdxs.map(i => [toSvgX(i), toY(watMA[i])]);

  return {
    paths: {
      oilH: ptsToPath(makePts(oil, histIdxs)),
      gasH: ptsToPath(makePts(gas, histIdxs)),
      watH: ptsToPath(makePts(water, histIdxs)),
      oilF: ptsToPath(makePts(oil, fcstIdxs)),
      gasF: ptsToPath(makePts(gas, fcstIdxs)),
      watF: ptsToPath(makePts(water, fcstIdxs)),
      oilMA: ptsToPath(oilMAPts),
      gasMA: ptsToPath(gasMAPts),
      watMA: ptsToPath(watMAPts),
      oilBandU: ptsToPath(bandPts(oil, [...histIdxs, ...fcstIdxs], 1.25)),
      oilBandL: ptsToPath(bandPts(oil, [...histIdxs, ...fcstIdxs], 0.75).reverse()),
      gasBandU: ptsToPath(bandPts(gas, [...histIdxs, ...fcstIdxs], 1.20)),
      gasBandL: ptsToPath(bandPts(gas, [...histIdxs, ...fcstIdxs], 0.80).reverse()),
      watBandU: ptsToPath(bandPts(water, [...histIdxs, ...fcstIdxs], 1.15)),
      watBandL: ptsToPath(bandPts(water, [...histIdxs, ...fcstIdxs], 0.85).reverse()),
    },
    todayX,
    // For hover interpolation
    curveDefs: [
      { id: "Oil", color: "#2a8a62", bright: "#3cc88a", pts: oilHoverPts, valPts: oilValPts, unit: "bbl/d" },
      { id: "Gas", color: "#a85050", bright: "#d06868", pts: gasHoverPts, valPts: gasValPts, unit: "mcf/d" },
      { id: "Water", color: "#4070a8", bright: "#5a90d0", pts: watHoverPts, valPts: watValPts, unit: "bbl/d" },
    ],
    // Raw arrays for econ
    oil, gas, water, todayIdx,
    // Summary stats
    latestOil: oil[todayIdx - 1],
    latestGas: gas[todayIdx - 1],
    latestWater: water[todayIdx - 1],
    avg7Oil: oilMA[todayIdx - 1],
    avg7Gas: gasMA[todayIdx - 1],
    avg7Water: watMA[todayIdx - 1],
    fcst7Oil: oil.slice(todayIdx, todayIdx + 7).reduce((s, v) => s + v, 0),
    fcst7Gas: gas.slice(todayIdx, todayIdx + 7).reduce((s, v) => s + v, 0),
  };
}

// ─── Default assumptions ───
export const DEFAULT_ECON = {
  oilPrice: 70, gasPrice: 3.0,
  oilDiff: 3.0, gasDiff: 0.3,
  oilMkt: 0, gasMkt: 0, oilTrans: 0, gasTrans: 0,
  gasShrink: 0.05,
  royalty: 0.20, workingInterest: 1.0, nriOverride: 0,
  oilSevTax: 0.046, gasSevTax: 0.075, adValTax: 0,
  fixedLoe: 4500, varLoe: 2.25, waterDisp: 1.25,
  chemicals: 0, electricity: 0, overhead: 0, baseMaint: 0,
  lowRateThreshold: 8, lowRateExtra: 900,
  capex: 0, abandonmentCost: 0,
  downtimePct: 0, discountRate: 0.10, windowDays: 60,
};

export const SCENARIOS = [
  { key: "Base", oilMult: 1.0, gasMult: 1.0, opexMult: 1.0 },
  { key: "Bear", oilMult: 0.85, gasMult: 0.80, opexMult: 1.05 },
  { key: "Bull", oilMult: 1.15, gasMult: 1.20, opexMult: 0.98 },
];

// ─── 5 Demo Leases ───
const LEASE_CONFIGS = [
  // 1. PROFITABLE — Stable Bakken producer, optimization upside
  {
    id: "S-15A", name: "WELL S-15A", operator: "Northern Plains E&P",
    basin: "Bakken", score: 92,
    oilBase: 120, gasBase: 200, waterBase: 150,
    oilDecline: 0.03, gasDecline: 0.04, waterGrowth: 0.02,
    oilNoise: 0.04, gasNoise: 0.03, waterNoise: 0.05,
    seed: 15,
    tags: ["Stable", "Cash Flow"],
    econOverrides: {},
    fieldNotes: [
      "Rod pump running smooth, no failures last 90 days.",
      "Fluid level survey shows 400ft above pump intake \u2014 room to increase SPM.",
      "Separator operating within spec. No carryover observed.",
      "Chemical program unchanged, no emulsion issues.",
      "Offset well (S-15B) came on with no interference detected.",
    ],
    opsFlags: ["optimization_candidate", "stable_lift", "excess_fluid_level"],
    annotations: [
      { label: "Stable plateau", x: 0.35, stream: "Oil", color: "#3cc888" },
      { label: "Optimization window", x: 0.70, stream: "Oil", color: "#5ab8c9" },
    ],
    inference: {
      signal: "Optimization headroom",
      diagnosis: "Stable base. Under-optimized lift.",
      directive: "\u2192 Increase SPM + monitor fluid level",
      summary: "Stable high-rate producer. Lift under-tuned \u2014 fluid level 400ft above intake indicates room to increase stroke rate.",
      whatMatters: "All streams flat and predictable. Main opportunity is ~5-10% uplift from pump speed, not a fix.",
      action: "Increase SPM 1-2 strokes, monitor fluid level 2 weeks. No workover needed.",
      econRead: "Strong cashflow. Margins absorb moderate price swings.",
      confidence: { Oil: "High", Gas: "High", Water: "High" },
      oneLiner: "\"Bump the pump, watch the level, leave it alone.\"",
      evidence: ["Fluid level 400ft above intake", "No rod failures 90 days", "Flat decline, type curve match"],
    },
  },
  // 2. PROFITABLE — Permian with gas constraint (fixable upside)
  {
    id: "W-07B", name: "WELL W-07B", operator: "Permian Ops LLC",
    basin: "Permian Basin", score: 78,
    oilBase: 85, gasBase: 120, waterBase: 200,
    oilDecline: 0.08, gasDecline: 0.15, waterGrowth: 0.05,
    oilNoise: 0.06, gasNoise: 0.22, waterNoise: 0.18,
    seed: 7,
    tags: ["Constrained", "Gas Issue"],
    econOverrides: {},
    fieldNotes: [
      "Gas rate swings 40-60% day to day \u2014 separator dumping intermittently.",
      "Backpressure spikes when gas plant takes curtailments.",
      "Oil rate holds 80-90 bbl/d despite gas variability.",
      "Water meter reads erratic \u2014 suspect foam/emulsion interference.",
      "Knockout drum filling faster than expected, possible liquid carryover into gas line.",
      "Compressor at pad site cycling on high-temp shutdown 2-3x per week.",
    ],
    opsFlags: ["surface_constraint", "separator_issue", "compressor_cycling", "measurement_uncertainty"],
    annotations: [
      { label: "Gas disruption", x: 0.30, stream: "Gas", color: "#d07070" },
      { label: "Oil baseline holds", x: 0.55, stream: "Oil", color: "#3cc888" },
    ],
    inference: {
      signal: "Surface constraint > reservoir decline",
      diagnosis: "Surface constraint, not reservoir decline. Gas-side facility issue.",
      directive: "\u2192 Fix gas handling first, then evaluate oil upside",
      summary: "Gas interruptions are facility-driven. Oil baseline stable \u2014 rules out downhole problem.",
      whatMatters: "Gas variability costing ~$800/day. Oil holding baseline. Water too noisy to read.",
      action: "Inspect separator / knockout / compressor. Fix high-temp shutdowns. Stabilize gas, then reassess.",
      econRead: "Profitable with gas losses. Fixing gas handling adds ~15-20% revenue, minimal capex.",
      confidence: { Oil: "Medium", Gas: "High", Water: "Low" },
      oneLiner: "\"Gas-side surface constraint. Fix the gas handling first.\"",
      evidence: ["Gas swings 40-60% daily", "Oil flat at 80-90", "Compressor cycling 2-3x/week", "Knockout drum overfilling"],
    },
  },
  // 3. MARGINAL — DJ Basin, steep decline, borderline economics
  {
    id: "K-31F", name: "WELL K-31F", operator: "DJ Basin Resources",
    basin: "DJ Basin", score: 52,
    oilBase: 35, gasBase: 50, waterBase: 180,
    oilDecline: 0.35, gasDecline: 0.28, waterGrowth: 0.10,
    oilNoise: 0.10, gasNoise: 0.09, waterNoise: 0.12,
    seed: 31,
    tags: ["Declining", "Marginal"],
    econOverrides: { fixedLoe: 5200, waterDisp: 1.80 },
    fieldNotes: [
      "Decline rate exceeds type curve by 2x \u2014 steeper than offset wells.",
      "Pumper reports intermittent pump-off events. Dyno card shows incomplete fillage.",
      "Fluid level dropped 200ft in 60 days.",
      "Tubing pressure trending up \u2014 possible scale restriction or parted rod.",
      "Water cut increasing: 65% \u2192 80% over 6 months.",
      "Last workover 14 months ago (rod replacement). No issues found then.",
    ],
    opsFlags: ["steep_decline", "pump_off_events", "fluid_level_drop", "possible_mechanical", "rising_water_cut"],
    annotations: [
      { label: "Decline accel", x: 0.25, stream: "Oil", color: "#d07070" },
      { label: "Water rising", x: 0.60, stream: "Water", color: "#5a90d0" },
    ],
    inference: {
      signal: "Mechanical constraint likely",
      diagnosis: "Decline too steep for reservoir. Probable mechanical issue.",
      directive: "\u2192 Pull dyno card + fluid level before committing to workover",
      summary: "Decline 2x steeper than type curve. Pattern consistent with scale restriction or pump fillage issue.",
      whatMatters: "If mechanical \u2192 workover recovers 15-25 bbl/d. If reservoir \u2192 poor ROI. Dyno card is the deciding evidence.",
      action: "Pull dyno card, run fluid level. If fillage <40% + tubing pressure elevated \u2192 pull tubing. Do not commit without diagnostics.",
      econRead: "Borderline. Daily CF near zero, trending negative. Workover justified only with >80% mechanical confidence.",
      confidence: { Oil: "Medium", Gas: "Medium", Water: "Medium" },
      oneLiner: "\"Smells mechanical. Pull a dyno card before deciding.\"",
      evidence: ["Decline 2x type curve", "Pump-off events", "Fluid level -200ft in 60d", "Tubing pressure rising", "WC 65%\u219280%"],
    },
  },
  // 4. UNPROFITABLE — Eagle Ford, water-dominated, losing money
  {
    id: "M-22C", name: "WELL M-22C", operator: "Eagle Ford Energy",
    basin: "Eagle Ford", score: 31,
    oilBase: 18, gasBase: 30, waterBase: 420,
    oilDecline: 0.22, gasDecline: 0.15, waterGrowth: 0.18,
    oilNoise: 0.08, gasNoise: 0.06, waterNoise: 0.08,
    seed: 22,
    tags: ["Water Burden", "Uneconomic"],
    econOverrides: { fixedLoe: 5800, waterDisp: 2.10, varLoe: 3.50 },
    fieldNotes: [
      "Water cut >93% and climbing. SWD hauling costs up 15% this quarter.",
      "Oil rate 16-20 bbl/d. Barely visible on daily reports.",
      "ESP running at max frequency. No room to increase rate.",
      "Persistent emulsion at treater \u2014 requiring double-dose demulsifier.",
      "SWD facility at capacity, overflow trucking adds $0.40/bbl.",
      "Tubing integrity unknown \u2014 last inspection 28 months ago.",
    ],
    opsFlags: ["water_dominated", "esp_maxed", "emulsion_issue", "swd_overcapacity", "infrastructure_stress"],
    annotations: [
      { label: "Water overwhelms", x: 0.40, stream: "Water", color: "#5a90d0" },
      { label: "Econ limit zone", x: 0.72, stream: "Oil", color: "#d07070" },
    ],
    inference: {
      signal: "Water economics dominate",
      diagnosis: "Water-dominated. Oil doesn't cover LOE. Losing money daily.",
      directive: "\u2192 Evaluate for shut-in immediately",
      summary: "ESP maxed handling water. Oil rate too low to justify operating cost structure.",
      whatMatters: "Water disposal exceeds oil revenue most days. Emulsion adds chemical cost. Not an optimization problem.",
      action: "Evaluate shut-in. Review: (1) SWD contract renegotiation, (2) emulsion chemical optimization, (3) lease-hold obligations.",
      econRead: "Operating at a loss. CF negative and worsening. Water disposal alone > gross oil revenue.",
      confidence: { Oil: "High", Gas: "Low", Water: "High" },
      oneLiner: "\"Underwater in every sense. Shut-in evaluation overdue.\"",
      evidence: ["WC >93%, climbing", "ESP at max", "SWD at capacity", "Double-dose chemical", "Oil 16-20, below LOE"],
    },
  },
  // 5. UNPROFITABLE — Marcellus gas well, price-crushed
  {
    id: "E-44D", name: "WELL E-44D", operator: "Appalachian Gas Co",
    basin: "Marcellus", score: 24,
    oilBase: 5, gasBase: 280, waterBase: 110,
    oilDecline: 0.40, gasDecline: 0.25, waterGrowth: 0.06,
    oilNoise: 0.15, gasNoise: 0.10, waterNoise: 0.06,
    seed: 44,
    tags: ["Gas Dependent", "Sub-Economic"],
    econOverrides: { fixedLoe: 6200, gasPrice: 2.20, overhead: 800 },
    fieldNotes: [
      "Gathering system pressure increased 40 psi over 6 months \u2014 downstream compressor bottlenecked.",
      "Gas decline faster than type curve \u2014 some may be backpressure-induced.",
      "Condensate rate negligible and erratic \u2014 below measurement confidence.",
      "Line pressure approaching flowing tubing pressure \u2014 well struggling to flow.",
      "Midstream imposed 20% volume curtailment last month (pipeline maintenance).",
      "LOE dominated by compressor rental and dehydration maintenance.",
      "Current strip ($2.20/mcf) well below $3.00 breakeven.",
    ],
    opsFlags: ["gathering_bottleneck", "backpressure_decline", "curtailment", "price_below_breakeven", "facility_limited"],
    annotations: [
      { label: "Backpressure rise", x: 0.35, stream: "Gas", color: "#d07070" },
      { label: "Sub-economic", x: 0.65, stream: "Gas", color: "#c89030" },
    ],
    inference: {
      signal: "Price below threshold + facility constraint",
      diagnosis: "Sub-economic at current strip. Gathering bottleneck compounding.",
      directive: "\u2192 Prepare P&A. Re-evaluate only if gas >$3.00",
      summary: "Declining reservoir pressure + rising gathering backpressure. Sub-threshold at current strip.",
      whatMatters: "Gas price is everything. At $2.20/mcf, loses money daily regardless of ops. Gathering makes it worse, not the root cause.",
      action: "No capex. If strip recovers >$3.00, re-evaluate + coordinate midstream. Otherwise prepare P&A.",
      econRead: "Deeply sub-economic. Fixed costs alone exceed revenue most days. Needs ~40% gas price recovery.",
      confidence: { Oil: "Low", Gas: "High", Water: "Medium" },
      oneLiner: "\"Pure gas bet, underwater. Prepare P&A unless strip recovers.\"",
      evidence: ["Gathering +40 psi / 6mo", "$2.20 vs $3.00 BE", "20% curtailment", "Near flowing THP", "Compressor dominates LOE"],
    },
  },
];

// Build chart data for each lease and export
export const DEMO_LEASES = LEASE_CONFIGS.map(cfg => ({
  ...cfg,
  chart: buildChartData(cfg),
  econDefaults: { ...DEFAULT_ECON, ...(cfg.econOverrides || {}) },
}));

// ─── Single-day econ row ───
function computeRow(oil, gas, water, e) {
  const uptime = 1 - Math.max(0, Math.min(1, e.downtimePct || 0));
  const oilBbl = oil * uptime;
  const gasMcf = gas * uptime;
  const watBbl = water * uptime;

  const netOilP = Math.max(0, (e.oilPrice||0)-(e.oilDiff||0)-(e.oilMkt||0)-(e.oilTrans||0));
  const netGasP = Math.max(0, (e.gasPrice||0)-(e.gasDiff||0)-(e.gasMkt||0)-(e.gasTrans||0));
  const salesGas = gasMcf * (1 - Math.max(0, Math.min(1, e.gasShrink||0)));

  const grossOil = oilBbl * netOilP;
  const grossGas = salesGas * netGasP;
  const grossRev = grossOil + grossGas;

  const royaltyAmt = grossRev * Math.max(0, Math.min(1, e.royalty||0));
  const oilSev = grossOil * (e.oilSevTax||0);
  const gasSev = grossGas * (e.gasSevTax||0);
  const adVal = grossRev * (e.adValTax||0);
  const totalTax = oilSev + gasSev + adVal;

  let wi = Math.max(0, Math.min(1, e.workingInterest||1));
  if ((e.nriOverride||0) > 0) wi = Math.min(1, e.nriOverride / Math.max(0.001, 1-(e.royalty||0)));

  const ownerRev = (grossRev - royaltyAmt - totalTax) * wi;
  const boe = oilBbl + gasMcf / 6;

  const opexRaw = (e.fixedLoe||0)/30 + (e.overhead||0)/30 + (e.baseMaint||0)/30
    + boe*(e.varLoe||0) + watBbl*(e.waterDisp||0) + boe*(e.chemicals||0)
    + boe*(e.electricity||0)
    + (boe < (e.lowRateThreshold||8) ? (e.lowRateExtra||0)/30 : 0);
  const opex = opexRaw * wi;

  return { oilBbl, gasMcf, watBbl, boe, grossRev, royaltyAmt, totalTax, wi, ownerRev, opex, cashflow: ownerRev - opex };
}

// ─── Full window computation ───
export function computeEconWindow(lease, econ, scenario) {
  const sc = scenario || SCENARIOS[0];
  const e = {
    ...econ,
    oilPrice: (econ.oilPrice||0)*sc.oilMult,
    gasPrice: (econ.gasPrice||0)*sc.gasMult,
    fixedLoe: (econ.fixedLoe||0)*sc.opexMult,
    varLoe: (econ.varLoe||0)*sc.opexMult,
    waterDisp: (econ.waterDisp||0)*sc.opexMult,
    overhead: (econ.overhead||0)*sc.opexMult,
    baseMaint: (econ.baseMaint||0)*sc.opexMult,
    chemicals: (econ.chemicals||0)*sc.opexMult,
    electricity: (econ.electricity||0)*sc.opexMult,
  };

  const days = Math.max(7, Math.min(365, econ.windowDays||60));
  const chart = lease.chart;
  const startIdx = chart.todayIdx;

  let cumCF = 0, cumPV = 0, firstNeg = null, payoutDay = null, payoutHit = false;
  let totalRev = 0, totalOpex = 0, totalBoe = 0;
  const rows = [];

  for (let i = 0; i < days; i++) {
    const di = startIdx + i;
    const oil = di < chart.oil.length ? chart.oil[di] : chart.oil[chart.oil.length-1] * 0.95;
    const gas = di < chart.gas.length ? chart.gas[di] : chart.gas[chart.gas.length-1] * 0.95;
    const water = di < chart.water.length ? chart.water[di] : chart.water[chart.water.length-1] * 1.02;

    let capex = 0;
    if (i === 0 && (econ.capex||0) > 0) capex += econ.capex;
    if (i === days-1 && (econ.abandonmentCost||0) > 0) capex += econ.abandonmentCost;

    const row = computeRow(oil, gas, water, e);
    const capexOwner = capex * row.wi;
    const cf = row.cashflow - capexOwner;
    cumCF += cf;
    const df = 1 / Math.pow(1 + (e.discountRate||0.10), i/365);
    cumPV += cf * df;
    totalRev += row.grossRev;
    totalOpex += row.opex;
    totalBoe += row.boe;

    if (cf < 0 && firstNeg === null) firstNeg = i;
    if (cumCF >= 0 && !payoutHit) { payoutDay = i; payoutHit = true; }
    rows.push({ day: i, ...row, capex: capexOwner, dailyCF: cf, cumCF, cumPV });
  }

  // Economic limit: 14-day rolling avg < 0
  let econLimit = null;
  for (let i = 13; i < rows.length; i++) {
    let sum = 0;
    for (let j = i-13; j <= i; j++) sum += rows[j].dailyCF;
    if (sum/14 < 0) { econLimit = i; break; }
  }

  const netback = totalBoe > 0 ? cumCF / totalBoe : 0;
  const loePerBoe = totalBoe > 0 ? totalOpex / totalBoe : 0;

  // Decision logic — handles unprofitable cases explicitly
  let decision, decisionDetail;
  if (cumPV < -500 && firstNeg !== null && firstNeg <= 3) {
    decision = "Shut-in candidate";
    decisionDetail = "Well is cash-flow negative from day " + (firstNeg+1) + ". PV is " + moneyFmt(cumPV) + ". Current operation destroys value.";
  } else if (econLimit !== null && econLimit <= 30) {
    decision = "Shut-in candidate";
    decisionDetail = "Economic limit reached at day " + econLimit + ". Rolling cashflow is negative.";
  } else if (econLimit !== null) {
    decision = "Monitor";
    decisionDetail = "Economic limit at day " + econLimit + ". Near-term cashflow is weakening.";
  } else if (firstNeg !== null && firstNeg <= 14) {
    decision = "Monitor";
    decisionDetail = "First negative cashflow at day " + firstNeg + ". Margins are thin.";
  } else if (cumPV < 0) {
    decision = "Monitor";
    decisionDetail = "Cumulative PV is negative (" + moneyFmt(cumPV) + "). Economics do not justify intervention.";
  } else {
    decision = "Operate";
    decisionDetail = "Cashflow positive. PV of " + moneyFmt(cumPV) + " over " + days + "-day window.";
  }

  const confidence = cumPV > totalRev * 0.15 ? "High"
    : cumPV > totalRev * 0.05 ? "Medium"
    : cumPV > 0 ? "Low" : "Very Low";

  return {
    rows, cumCF, cumPV, firstNeg, payoutDay: payoutHit ? payoutDay : null,
    econLimit, totalRev, totalOpex, totalBoe, netback, loePerBoe,
    decision, decisionDetail, confidence,
  };
}

// ─── Breakeven oil price (bisection) ───
export function computeBreakevenOil(lease, econ, scenario) {
  let lo = 0, hi = 200;
  for (let iter = 0; iter < 40; iter++) {
    const mid = (lo + hi) / 2;
    const r = computeEconWindow(lease, { ...econ, oilPrice: mid }, scenario);
    if (r.cumPV > 0) hi = mid; else lo = mid;
    if (hi - lo < 0.05) break;
  }
  return Number.isFinite((lo+hi)/2) ? (lo+hi)/2 : null;
}

// ─── Reasoning engine ───
export function generateReasoning(econResult, lease, econ, defaults) {
  const msgs = [];
  const r = econResult;
  const d = defaults || DEFAULT_ECON;

  msgs.push({ type: "insight", text: lease.inference.diagnosis });
  msgs.push({ type: "takeaway", text: lease.inference.oneLiner });

  // Oil price shift
  const oilDelta = ((econ.oilPrice||0) - d.oilPrice) / d.oilPrice;
  if (oilDelta > 0.12) {
    msgs.push({ type: "shift", text: "Oil $" + (econ.oilPrice||0).toFixed(0) + " \u2192 " + (r.cumPV > 0 ? "improves resilience, PV positive" : "helps but PV still negative") });
  } else if (oilDelta < -0.12) {
    msgs.push({ type: "shift", text: "Oil $" + (econ.oilPrice||0).toFixed(0) + " \u2192 " + (r.decision === "Operate" ? "margins compress, still operable" : "economics challenged") });
  }

  // Gas price
  const gasDelta = ((econ.gasPrice||0) - d.gasPrice) / d.gasPrice;
  if (gasDelta > 0.15) {
    msgs.push({ type: "insight", text: "Gas price up \u2192 " + (lease.gasBase > 200 ? "material impact (gas-weighted)" : "secondary impact") });
  } else if (gasDelta < -0.15) {
    msgs.push({ type: "insight", text: "Gas pricing weak \u2192 " + (lease.gasBase > 200 ? "primary risk (gas-dominant)" : "limited exposure") });
  }

  // Water cost burden
  const dailyOpex = r.totalOpex / Math.max(1, econ.windowDays||60);
  const dailyWater = lease.waterBase * (econ.waterDisp||0);
  const waterShare = dailyOpex > 0 ? dailyWater / dailyOpex : 0;
  if (waterShare > 0.30) {
    msgs.push({ type: "insight", text: "Water handling " + (waterShare*100).toFixed(0) + "% of opex \u2014 dominant cost driver" });
  } else if (waterShare > 0.20) {
    msgs.push({ type: "insight", text: "Water disposal elevated at " + (waterShare*100).toFixed(0) + "% of opex" });
  }

  // Downtime
  if ((econ.downtimePct||0) > 0.08) {
    msgs.push({ type: "shift", text: ((econ.downtimePct||0)*100).toFixed(0) + "% downtime \u2192 operational fragility is primary risk" });
  }

  // Capex / ROI
  if ((econ.capex||0) > 0) {
    if (r.cumPV > 0) {
      const roi = r.cumPV / econ.capex;
      msgs.push({ type: "insight", text: "Capex ROI " + roi.toFixed(1) + "x \u2014 " + (roi < 1.5 ? "marginal, risky" : "supports intervention") });
    } else {
      msgs.push({ type: "shift", text: "PV negative + $" + ((econ.capex||0)/1000).toFixed(0) + "k capex \u2192 does not pay out" });
    }
  }

  // PV direction
  if (r.cumPV < -2000) {
    msgs.push({ type: "shift", text: "PV deeply negative (" + moneyFmt(r.cumPV) + "). Destroying value daily." });
  } else if (r.cumPV < 0) {
    msgs.push({ type: "shift", text: "PV negative (" + moneyFmt(r.cumPV) + "). Economics don't justify operation." });
  } else if (r.cumPV > 0 && r.totalRev > 0 && r.cumPV < r.totalRev * 0.05) {
    msgs.push({ type: "insight", text: "PV positive but thin \u2014 vulnerable to any adverse move" });
  }

  // Economic limit
  if (r.econLimit !== null) {
    msgs.push({ type: "shift", text: "Econ limit at day " + r.econLimit + " \u2014 destroys value beyond" });
  }

  // First negative CF
  if (r.firstNeg !== null && r.firstNeg <= 3) {
    msgs.push({ type: "shift", text: "CF negative from day " + r.firstNeg + " \u2014 not covering opex" });
  }

  // Composite
  if (r.decision === "Shut-in candidate") {
    msgs.push({ type: "takeaway", text: "\"Numbers don't support continued operation.\"" });
  } else if (r.decision === "Monitor") {
    msgs.push({ type: "takeaway", text: "\"Keep running, watch closely. Any disruption tips negative.\"" });
  }

  return msgs;
}

// ─── Preset prompt answers ───
export const PRESET_PROMPTS = [
  { key: "why_changed", label: "Why did recommendation change?" },
  { key: "downside", label: "What is driving downside?" },
  { key: "workover", label: "Is workover justified?" },
  { key: "ops_inspect", label: "What should ops inspect first?" },
];

export function answerPrompt(promptKey, econResult, lease, econ) {
  const r = econResult;
  switch (promptKey) {
    case "why_changed":
      if (r.decision === "Shut-in candidate")
        return "PV is " + moneyFmt(r.cumPV) + " and cashflow turns negative at day " + (r.firstNeg ?? "N/A") + ". The well is not covering operating costs under these assumptions.";
      if (r.decision === "Monitor")
        return "Economics are marginal. First negative cashflow at day " + (r.firstNeg ?? "N/A") + " with PV of " + moneyFmt(r.cumPV) + ". The margin of safety is thin.";
      return "Under current assumptions, the well remains economic with PV of " + moneyFmt(r.cumPV) + ". No recommendation change triggered.";
    case "downside": {
      const waterBurden = lease.waterBase * (econ.waterDisp||0);
      const fixedBurden = (econ.fixedLoe||0) / 30;
      if (waterBurden > fixedBurden)
        return "Water disposal at ~$" + waterBurden.toFixed(0) + "/day is the dominant cost. This exceeds fixed LOE of ~$" + fixedBurden.toFixed(0) + "/day. Reducing water cost or water cut is the highest-impact lever.";
      return "Fixed LOE at ~$" + fixedBurden.toFixed(0) + "/day is the largest bucket. At low production rates, fixed costs dominate and margins compress rapidly.";
    }
    case "workover":
      if (r.cumPV > (econ.capex||0) * 2 && r.cumPV > 5000)
        return "At current pricing, workover ROI exceeds 2x. The investment case is supported if diagnostics confirm a mechanical cause.";
      if (r.cumPV > 0)
        return "Workover ROI is positive but modest (PV: " + moneyFmt(r.cumPV) + "). Proceed only if low-risk and diagnostics are favorable.";
      return "Workover is not justified. PV is " + moneyFmt(r.cumPV) + ". Additional capex would deepen the loss. Defer until pricing improves.";
    case "ops_inspect":
      return lease.inference.action;
    default:
      return "";
  }
}

function moneyFmt(n) {
  if (!Number.isFinite(n)) return "\u2014";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
